import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import SponsorPlaceholder from "../components/SponsorPlaceholder";
import ScrollToTop from "../components/ScrollToTop";
import { colors } from "../styles/colors";

export default function EmploymentHub() {
  const employmentClauses = [
    { path: "/explain-notice-period-clause", title: "Notice Period" },
    { path: "/explain-probation-period-clause", title: "Probation Period" },
    { path: "/explain-non-compete-clause", title: "Non-Compete" },
    { path: "/explain-confidentiality-clause", title: "Confidentiality" },
    { path: "/explain-payment-in-lieu-of-notice-clause", title: "Payment in Lieu of Notice" },
    { path: "/explain-fixed-term-employment-clause", title: "Fixed Term Employment" },
    { path: "/explain-termination-for-cause-clause", title: "Termination for Cause" },
    { path: "/explain-redundancy-clause", title: "Redundancy" },
    { path: "/explain-leave-entitlements-clause", title: "Leave Entitlements" },
    { path: "/explain-restraint-of-trade-clause", title: "Restraint of Trade" },
  ];

  return (
    <div className="min-h-screen" style={{ backgroundColor: colors.background }}>
      <header 
        className="px-6 md:px-12 lg:px-24 py-6" 
        style={{ borderBottom: `1px solid ${colors.border}` }}
      >
        <Link 
          to="/" 
          className="inline-flex items-center gap-3 hover:opacity-80 transition-opacity"
        >
          <img 
            src="/clauseclear-logo.png" 
            alt="ClauseClear" 
            className="h-10 object-contain"
          />
        </Link>
      </header>

      <article className="px-6 md:px-12 lg:px-24 py-16">
        <div className="max-w-4xl mx-auto">
          <h1 
            className="text-4xl md:text-5xl font-medium tracking-tight leading-[1.1] mb-8" 
            style={{ color: colors.text }}
          >
            Employment Contract Clauses Explained in Plain English
          </h1>

          <p className="text-base md:text-lg leading-relaxed mb-12" style={{ color: colors.textMuted }}>
            Plain-English explanations of common employment contract clauses, what they mean, and how they affect employees. Each guide breaks down complex legal language into clear, straightforward information.
          </p>

          <div className="mb-12">
            <Link 
              to="/clauses" 
              className="inline-flex items-center gap-2 text-base font-medium hover:opacity-80 transition-opacity mb-8" 
              style={{ color: colors.accent }}
            >
              ← Back to all clauses
            </Link>
          </div>

          <section className="mb-12">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {employmentClauses.map((clause) => (
                <Link
                  key={clause.path}
                  to={clause.path}
                  className="flex items-center justify-between p-6 rounded-xl transition-all group"
                  style={{ backgroundColor: colors.backgroundLight, border: `1px solid ${colors.border}` }}
                >
                  <span className="text-base font-medium" style={{ color: colors.text }}>
                    {clause.title}
                  </span>
                  <ArrowRight 
                    className="w-5 h-5 opacity-0 group-hover:opacity-100 transition-opacity" 
                    style={{ color: colors.accent }} 
                  />
                </Link>
              ))}
            </div>
          </section>

          <div 
            className="text-center p-8 rounded-2xl" 
            style={{ backgroundColor: colors.backgroundLight, border: `1px solid ${colors.border}` }}
          >
            <p className="text-lg mb-4" style={{ color: colors.textMuted }}>
              Have a specific clause you want to understand?
            </p>
            <Link 
              to="/" 
              className="inline-flex items-center gap-2 hover:opacity-90 rounded-full px-8 py-4 text-lg font-medium transition-all"
              style={{ backgroundColor: colors.accent, color: colors.background }}
            >
              Try ClauseClear
              <ArrowRight className="w-5 h-5" />
            </Link>
          </div>

          <SponsorPlaceholder variant="hub" />
        </div>
      </article>

      <footer 
        className="px-6 md:px-12 lg:px-24 py-12 text-center" 
        style={{ borderTop: `1px solid ${colors.border}`, backgroundColor: colors.backgroundDark }}
      >
        <p className="text-sm mb-2" style={{ color: colors.textDim }}>
          ClauseClear is an educational tool. It does not provide legal advice.
        </p>
        <a 
          href="https://buymeacoffee.com/jamesmoneyz" 
          target="_blank" 
          rel="noopener noreferrer" 
          className="text-base font-medium hover:opacity-80 transition-opacity" 
          style={{ color: colors.accent }}
        >
          Support the project
        </a>
      </footer>

      <ScrollToTop />
    </div>
  );
}
